import type { CustomListForm } from '@api'
import { DashboardCustomListRestEndpointService } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

export const useCustomListMutations = () => {
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const toast = useToast()

  const createCustomListMutation = useMutation({
    mutationFn: (payload: CustomListForm) =>
      DashboardCustomListRestEndpointService.dashboardCreateCustomList({
        creatorId: creatorId!,
        requestBody: payload,
      }),
    onSuccess: () => {
      toast.success('Custom list created successfully')
      queryClient.invalidateQueries({
        queryKey: ['creator', creatorId, 'custom-lists'],
      })
    },
    onError: (error) => {
      handleError(error, 'Failed to create custom list')
    },
  })

  const deleteCustomListMutation = useMutation({
    mutationFn: (listId: number) =>
      DashboardCustomListRestEndpointService.dashboardDeleteCustomList({
        creatorId: creatorId!,
        listId,
      }),
    onSuccess: () => {
      toast.success('Custom list deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['creator', creatorId, 'custom-lists'],
      })
    },
    onError: (error) => {
      handleError(error, 'Failed to delete custom list')
    },
  })

  const editCustomListMutation = useMutation({
    mutationFn: ({
      listId,
      payload,
    }: {
      listId: number
      payload: CustomListForm
    }) =>
      DashboardCustomListRestEndpointService.dashboardUpdateCustomList({
        creatorId: creatorId!,
        listId,
        requestBody: payload,
      }),
    onSuccess: () => {
      toast.success('Custom list updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['creator', creatorId, 'custom-lists'],
      })
    },
    onError: (error) => {
      handleError(error, 'Failed to update custom list')
    },
  })

  return {
    createCustomListMutation,
    deleteCustomListMutation,
    editCustomListMutation,
  }
}
